import { Link } from 'react-router-dom'
import { Users, CheckCircle, Clock, Award, Target, TrendingUp } from 'lucide-react'
import Hero from '../../components/Hero'
import SEO from '../../components/SEO'

export default function Leadership() {
  const requirements = [
    'High school diploma or equivalent',
    'Personal statement outlining leadership goals',
    'Resume or CV',
    'Two letters of recommendation',
    'Completed application form'
  ]

  const courses = [
    'Leadership Theory & Practice',
    'Organizational Management',
    'Strategic Planning',
    'Ethical Leadership',
    'Communication & Public Speaking',
    'Conflict Resolution',
    'Team Building & Motivation',
    'Change Management'
  ]

  const careers = [
    'Executive Leader',
    'Non-Profit Director',
    'Church Administrator',
    'Community Organizer',
    'Business Manager',
    'Educational Leader',
    'Ministry Coordinator',
    'Organizational Consultant'
  ]

  return (
    <>
      <SEO 
        title="Leadership Program - Rudovia Life College"
        description="Develop transformational leadership skills at Rudovia Life College. Online and on-campus leadership courses rooted in Christian values and ethical principles."
        keywords="leadership courses online, leadership training Africa, Christian leadership, organizational management, leadership diploma Zimbabwe"
      />
      <div className="pt-20">
        <Hero
          title="Leadership Program"
          subtitle="Develop transformational leadership skills rooted in Christian values"
          image="/assets/images/pexels-kampus-7551674.jpg"
          height="h-[400px]"
          primaryButton={{ text: 'Apply Now', link: '/admissions' }}
        />

        <section className="section-padding bg-white">
          <div className="container-custom">
            <div className="grid md:grid-cols-2 gap-12 items-center mb-16">
              <div>
                <img 
                  src="/assets/images/pexels-kampus-7551674.jpg" 
                  alt="Leadership Training" 
                  className="rounded-2xl shadow-2xl"
                />
              </div>
              <div>
                <h2 className="heading-2 mb-6">Program Overview</h2>
                <p className="text-lg text-gray-700 mb-4 leading-relaxed">
                  Our Leadership program equips students with the knowledge, skills, and character needed to lead effectively in various organizational contexts. Combining leadership theory with practical application, the program develops leaders who can inspire teams, drive change, and make ethical decisions.
                </p>
                <p className="text-lg text-gray-700 mb-4 leading-relaxed">
                  Rooted in Christian principles of servant leadership, integrity, and stewardship, graduates are prepared to lead with vision and compassion in churches, businesses, non-profits, and community organizations.
                </p>
                <div className="flex items-center space-x-8 mt-8">
                  <div className="flex items-center space-x-2">
                    <Clock className="w-6 h-6 text-secondary" />
                    <div>
                      <div className="font-bold text-primary">Duration</div>
                      <div className="text-gray-600">2-3 Years</div>
                    </div>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Award className="w-6 h-6 text-secondary" />
                    <div>
                      <div className="font-bold text-primary">Qualification</div>
                      <div className="text-gray-600">Diploma in Leadership</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="grid md:grid-cols-3 gap-8">
              <div className="bg-gray-50 rounded-xl p-8">
                <Users className="w-12 h-12 text-secondary mb-4" />
                <h3 className="text-2xl font-bold text-primary mb-4">Core Courses</h3>
                <ul className="space-y-2">
                  {courses.map((course, index) => (
                    <li key={index} className="flex items-start space-x-2">
                      <CheckCircle className="w-5 h-5 text-secondary flex-shrink-0 mt-1" />
                      <span className="text-gray-700">{course}</span>
                    </li>
                  ))}
                </ul>
              </div>

              <div className="bg-gray-50 rounded-xl p-8">
                <Target className="w-12 h-12 text-secondary mb-4" />
                <h3 className="text-2xl font-bold text-primary mb-4">Admission Requirements</h3>
                <ul className="space-y-2">
                  {requirements.map((req, index) => (
                    <li key={index} className="flex items-start space-x-2">
                      <CheckCircle className="w-5 h-5 text-secondary flex-shrink-0 mt-1" />
                      <span className="text-gray-700">{req}</span>
                    </li>
                  ))}
                </ul>
              </div>

              <div className="bg-gray-50 rounded-xl p-8">
                <TrendingUp className="w-12 h-12 text-secondary mb-4" />
                <h3 className="text-2xl font-bold text-primary mb-4">Career Opportunities</h3>
                <ul className="space-y-2">
                  {careers.map((career, index) => (
                    <li key={index} className="flex items-start space-x-2">
                      <CheckCircle className="w-5 h-5 text-secondary flex-shrink-0 mt-1" />
                      <span className="text-gray-700">{career}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </section>

        <section className="section-padding bg-primary text-white">
          <div className="container-custom">
            <div className="max-w-4xl mx-auto text-center">
              <h2 className="text-4xl md:text-5xl font-bold mb-6">Why Study Leadership at Rudovia?</h2>
              <div className="grid md:grid-cols-2 gap-8 mt-12">
                <div className="text-left">
                  <h3 className="text-2xl font-bold mb-3 text-secondary">Servant Leadership</h3>
                  <p className="text-gray-200">
                    Learn to lead with humility, integrity, and a heart for serving others—principles rooted in Christian values.
                  </p>
                </div>
                <div className="text-left">
                  <h3 className="text-2xl font-bold mb-3 text-secondary">Practical Skills</h3>
                  <p className="text-gray-200">
                    Develop real-world leadership competencies through case studies, projects, and hands-on experience.
                  </p>
                </div>
                <div className="text-left">
                  <h3 className="text-2xl font-bold mb-3 text-secondary">Experienced Mentors</h3>
                  <p className="text-gray-200">
                    Learn from successful leaders who bring decades of experience in business, ministry, and community development.
                  </p>
                </div>
                <div className="text-left">
                  <h3 className="text-2xl font-bold mb-3 text-secondary">Flexible Learning</h3>
                  <p className="text-gray-200">
                    Choose from on-campus or online learning options designed to fit your schedule and learning preferences.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="section-padding bg-white">
          <div className="container-custom text-center">
            <h2 className="heading-2 mb-6">Become the Leader You're Called to Be</h2>
            <p className="text-xl text-gray-600 mb-8 max-w-2xl mx-auto">
              Transform your leadership potential into impact. Apply to our Leadership program today.
            </p>
            <Link to="/admissions" className="btn-primary">
              Apply Now
            </Link>
          </div>
        </section>
      </div>
    </>
  )
}
